import { Node } from 'reactflow';
import { stableButtonId } from '@shared/flow-utils';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Trash2, Check, MessageCircle, Zap, GitFork, Clock, Shuffle, Users, Bot, ClipboardList, Globe, FileText, Flag, Plus, Loader2, AlertTriangle } from 'lucide-react';
import { MessageEditor } from './MessageEditor';
import { resumoDoNo, pendenciaDoNo } from '@shared/flow-node-summary';
import { CLOSE_REASONS } from '@shared/close-reasons';
import { trpc } from '@/lib/trpc';
import { useState } from 'react';
import { toast } from 'sonner';
import { toUserFriendlyError } from '@/lib/error-messages';

interface NodeConfigPanelProps {
  node: Node | null;
  onClose: () => void;
  onUpdate: (nodeId: string, data: any) => void;
  onDelete?: (nodeId: string) => void;
  onSaveNode?: () => void;
}

const NODE_META: Record<string, { icon: any; label: string; accent: string; bar: string }> = {
  content:          { icon: MessageCircle, label: 'Enviar mensagem',        accent: 'text-green-600',  bar: 'bg-green-500' },
  action:           { icon: Zap,           label: 'Ação',                   accent: 'text-emerald-600',bar: 'bg-emerald-500' },
  condition:        { icon: GitFork,       label: 'Dividir por condição',   accent: 'text-yellow-600', bar: 'bg-yellow-500' },
  delay:            { icon: Clock,         label: 'Esperar',                accent: 'text-purple-600', bar: 'bg-purple-500' },
  randomizer:       { icon: Shuffle,       label: 'Dividir o tráfego',      accent: 'text-orange-600', bar: 'bg-orange-500' },
  target:           { icon: Users,         label: 'Público-Alvo',           accent: 'text-gray-500',   bar: 'bg-gray-400' },
  ai_assistant:     { icon: Bot,           label: 'Responder com IA',       accent: 'text-indigo-600', bar: 'bg-indigo-500' },
  ai_classifier:    { icon: GitFork,       label: 'Separar por assunto',    accent: 'text-cyan-600',   bar: 'bg-cyan-500' },
  data_collection:  { icon: ClipboardList, label: 'Fazer uma pergunta',     accent: 'text-teal-600',   bar: 'bg-teal-500' },
  webhook_request:  { icon: Globe,         label: 'Enviar para outro sistema', accent: 'text-rose-600', bar: 'bg-rose-500' },
  end:              { icon: Flag,          label: 'Fim do fluxo',           accent: 'text-gray-500',   bar: 'bg-gray-400' },
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider pt-1 pb-0.5">{children}</p>;
}

const STATUS_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  approved: { label: 'Aprovado', variant: 'default' },
  pending: { label: 'Pendente', variant: 'secondary' },
  rejected: { label: 'Rejeitado', variant: 'destructive' },
  draft: { label: 'Rascunho', variant: 'outline' },
};

// Hook compartilhado entre TagPicker (single) e TagMultiPicker.
// Centraliza fetch da lista, busca textual e mutation de criação.
function useTagPickerState() {
  const utils = trpc.useUtils();
  const { data: tagsList = [] } = trpc.tags.list.useQuery() as { data?: Array<{ id: number; name: string }> };
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const createMut = trpc.tags.create.useMutation({
    onSuccess: () => {
      utils.tags.list.invalidate();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, 'Erro ao criar tag')),
  });

  const filteredTags = search.trim()
    ? tagsList.filter((t) => t.name.toLowerCase().includes(search.trim().toLowerCase()))
    : tagsList;

  return { tagsList, filteredTags, search, setSearch, creating, setCreating, newName, setNewName, createMut };
}

// Picker de tag (single) com busca e criação inline. Usado em casos onde só
// faz sentido escolher 1 tag (ex: nó público-alvo).
function TagPicker({
  value,
  onChange,
  onChangeName,
}: {
  value: number | null;
  onChange: (tagId: number | null) => void;
  onChangeName?: (name: string) => void;
}) {
  const { tagsList, filteredTags, search, setSearch, creating, setCreating, newName, setNewName, createMut } = useTagPickerState();
  const selected = tagsList.find((t) => t.id === value) ?? null;

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) return;
    const existing = tagsList.find((t) => t.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      onChange(existing.id);
      onChangeName?.(existing.name);
      setNewName('');
      setCreating(false);
      toast.info(`Tag "${existing.name}" já existe — selecionada`);
      return;
    }
    createMut.mutate(
      { name },
      {
        onSuccess: (created: any) => {
          const id = created?.id ?? created?.[0]?.id;
          const tagName = created?.name ?? created?.[0]?.name ?? name;
          if (id) {
            onChange(id);
            onChangeName?.(tagName);
          }
          setNewName('');
          setCreating(false);
          toast.success('Tag criada');
        },
      },
    );
  };

  return (
    <div className="space-y-2">
      {/* Tag selecionada (chip) */}
      {selected && (
        <div className="flex items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-1.5">
          <span className="text-xs font-medium flex-1 truncate">{selected.name}</span>
          <button
            type="button"
            onClick={() => { onChange(null); onChangeName?.(''); }}
            className="text-muted-foreground hover:text-destructive"
            aria-label="Remover tag"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Busca — a lista só aparece quando há texto na busca, evitando dump
          das centenas de tags por padrão. */}
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar tag..."
        className="text-sm h-8"
      />

      {/* Lista de opções (apenas quando há busca) */}
      {search.trim() && (
        <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
          {filteredTags.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">Nenhuma tag com "{search}"</div>
          ) : (
            filteredTags.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => { onChange(t.id); onChangeName?.(t.name); setSearch(''); }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted/60 transition-colors ${value === t.id ? 'bg-primary/10 font-medium' : ''}`}
              >
                {t.name}
              </button>
            ))
          )}
        </div>
      )}

      {/* Criar nova */}
      {creating ? (
        <div className="flex gap-1.5">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            placeholder="Nome da nova tag"
            className="text-sm h-8 flex-1"
            autoFocus
          />
          <Button type="button" variant="default" size="sm" className="h-8" disabled={createMut.isPending || !newName.trim()} onClick={handleCreate}>
            {createMut.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-8" onClick={() => { setCreating(false); setNewName(''); }}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setCreating(true)}>
          <Plus className="w-3 h-3" /> Criar nova tag
        </Button>
      )}

      {tagsList.length === 0 && !creating && (
        <p className="text-[11px] text-muted-foreground">Nenhuma tag cadastrada — crie uma nova acima.</p>
      )}
    </div>
  );
}

// Picker de múltiplas tags com busca e criação inline. Usado quando o nó
// pode aplicar várias tags de uma vez (ex: ação Adicionar/Remover Tag).
function TagMultiPicker({
  value,
  onChange,
  onChangeNames,
}: {
  value: number[];
  onChange: (tagIds: number[]) => void;
  onChangeNames?: (names: string[]) => void;
}) {
  const { tagsList, filteredTags, search, setSearch, creating, setCreating, newName, setNewName, createMut } = useTagPickerState();
  const selectedSet = new Set(value);
  const selectedTags = tagsList.filter((t) => selectedSet.has(t.id));

  const setSelected = (next: number[]) => {
    onChange(next);
    if (onChangeNames) {
      const names = tagsList.filter((t) => next.includes(t.id)).map((t) => t.name);
      onChangeNames(names);
    }
  };

  const toggle = (tagId: number) => {
    if (selectedSet.has(tagId)) {
      setSelected(value.filter((id) => id !== tagId));
    } else {
      setSelected([...value, tagId]);
    }
  };

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) return;
    const existing = tagsList.find((t) => t.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!selectedSet.has(existing.id)) setSelected([...value, existing.id]);
      setNewName('');
      setCreating(false);
      toast.info(`Tag "${existing.name}" já existe — selecionada`);
      return;
    }
    createMut.mutate(
      { name },
      {
        onSuccess: (created: any) => {
          const id = created?.id ?? created?.[0]?.id;
          if (id) setSelected([...value, id]);
          setNewName('');
          setCreating(false);
          toast.success('Tag criada');
        },
      },
    );
  };

  return (
    <div className="space-y-2">
      {/* Chips de tags selecionadas */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedTags.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-0.5 text-xs">
              {t.name}
              <button
                type="button"
                onClick={() => toggle(t.id)}
                className="text-muted-foreground hover:text-destructive"
                aria-label={`Remover ${t.name}`}
              >
                <Trash2 className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Busca — a lista só aparece quando há texto. */}
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar tag..."
        className="text-sm h-8"
      />

      {/* Lista (clicável p/ alternar). Aparece só quando há busca. */}
      {search.trim() && (
        <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
          {filteredTags.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">Nenhuma tag com "{search}"</div>
          ) : (
            filteredTags.map((t) => {
              const isSelected = selectedSet.has(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggle(t.id)}
                  className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-muted/60 transition-colors ${isSelected ? 'bg-primary/10 font-medium' : ''}`}
                >
                  <span className={`w-3 h-3 rounded border flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/40'}`}>
                    {isSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                  </span>
                  {t.name}
                </button>
              );
            })
          )}
        </div>
      )}

      {/* Criar nova */}
      {creating ? (
        <div className="flex gap-1.5">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            placeholder="Nome da nova tag"
            className="text-sm h-8 flex-1"
            autoFocus
          />
          <Button type="button" variant="default" size="sm" className="h-8" disabled={createMut.isPending || !newName.trim()} onClick={handleCreate}>
            {createMut.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-8" onClick={() => { setCreating(false); setNewName(''); }}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setCreating(true)}>
          <Plus className="w-3 h-3" /> Criar nova tag
        </Button>
      )}
    </div>
  );
}

function TargetConfig({ node, onUpdate }: { node: Node; onUpdate: (field: string | Record<string, any>, value?: any) => void }) {
  const targetType = node.data.targetType || 'segment';
  const { data: segments = [] } = trpc.segments.list.useQuery(undefined, { enabled: targetType === 'segment' }) as { data?: Array<{ id: number; name: string }> };
  const { data: filters = [] } = trpc.advancedFilters.list.useQuery(undefined, { enabled: targetType === 'filter' }) as { data?: Array<{ id: number; name: string }> };

  return (
    <div className="space-y-3">
      <div>
        <SectionLabel>Tipo</SectionLabel>
        <Select value={targetType} onValueChange={(v) => onUpdate({ targetType: v, targetId: null, targetValue: '' })}>
          <SelectTrigger className="text-sm h-9 mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="segment">Segmento</SelectItem>
            <SelectItem value="tag">Tag</SelectItem>
            <SelectItem value="filter">Filtro Avançado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {targetType === 'tag' && (
        <div>
          <SectionLabel>Tag</SectionLabel>
          <TagPicker
            value={node.data.targetId ?? null}
            onChange={(tagId) => onUpdate('targetId', tagId)}
            onChangeName={(name) => onUpdate('targetValue', name)}
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Escolha uma tag existente ou crie uma nova.
          </p>
        </div>
      )}

      {targetType === 'segment' && (
        <div>
          <SectionLabel>Segmento</SectionLabel>
          <Select
            value={node.data.targetId ? String(node.data.targetId) : ''}
            onValueChange={(v) => {
              const id = Number(v);
              const seg = segments.find((s) => s.id === id);
              onUpdate({ targetId: id, targetValue: seg?.name ?? '' });
            }}
          >
            <SelectTrigger className="text-sm h-9 mt-1">
              <SelectValue placeholder={segments.length === 0 ? 'Nenhum segmento cadastrado' : 'Selecionar segmento...'} />
            </SelectTrigger>
            <SelectContent>
              {segments.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
              ))}
              {segments.length === 0 && (
                <div className="px-3 py-2 text-xs text-muted-foreground">Nenhum segmento — crie em Contatos &rarr; Segmentos</div>
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {targetType === 'filter' && (
        <div>
          <SectionLabel>Filtro Avançado</SectionLabel>
          <Select
            value={node.data.targetId ? String(node.data.targetId) : ''}
            onValueChange={(v) => {
              const id = Number(v);
              const f = filters.find((x) => x.id === id);
              onUpdate({ targetId: id, targetValue: f?.name ?? '' });
            }}
          >
            <SelectTrigger className="text-sm h-9 mt-1">
              <SelectValue placeholder={filters.length === 0 ? 'Nenhum filtro cadastrado' : 'Selecionar filtro...'} />
            </SelectTrigger>
            <SelectContent>
              {filters.map((f) => (
                <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>
              ))}
              {filters.length === 0 && (
                <div className="px-3 py-2 text-xs text-muted-foreground">Nenhum filtro — crie em Contatos &rarr; Filtros</div>
              )}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

/**
 * Para quem a conversa vai.
 *
 * O campo antigo era um input de texto livre com placeholder
 * "Ex: field-value, webhook-url, agent-id": pedia o ID numérico de um usuário
 * que a tela não mostrava em lugar nenhum. E o motor nem lia — a ação caía no
 * `default:` do switch e devolvia sucesso sem atribuir ninguém.
 *
 * Guardamos o nome junto com o id (`assignedUserName`) só para o cartão do
 * canvas poder dizer "Entrega a conversa para Ana" sem buscar a lista de
 * usuários. Quem manda na execução é sempre o id, revalidado no servidor.
 */
function AtribuicaoConfig({
  node,
  onUpdate,
}: {
  node: Node;
  onUpdate: (field: string | Record<string, any>, value?: any) => void;
}) {
  const { data: usuarios = [], isLoading, isError } = trpc.users.list.useQuery(undefined, {
    retry: false,
  }) as { data?: Array<{ id: number; name: string | null; email: string; isActive: boolean }>; isLoading: boolean; isError: boolean };

  const ativos = usuarios.filter((u) => u.isActive);
  const modo: 'auto' | 'user' | 'team' =
    node.data.assignMode === 'team' ? 'team'
    : node.data.assignMode === 'user' ? 'user'
    : node.data.assignedUserId ? 'user'
    : 'auto';
  const ehHandoff = node.data.actionType === 'handoff';
  const paraRobo = node.data.stopBot ?? ehHandoff;

  return (
    <div className="space-y-3">
      <div>
        <SectionLabel>Quem recebe a conversa</SectionLabel>
        <div className="grid grid-cols-2 gap-1.5 mt-1">
          <button
            type="button"
            onClick={() => onUpdate({ assignMode: 'auto', assignedUserId: null, assignedUserName: '' })}
            className={`rounded-lg px-3 py-2 text-left text-xs transition-colors border ${
              modo === 'auto' ? 'bg-primary/[0.08] border-primary/30 font-medium' : 'hover:bg-muted/60 border-transparent'
            }`}
          >
            Distribuição automática
          </button>
          <button
            type="button"
            onClick={() => onUpdate('assignMode', 'user')}
            className={`rounded-lg px-3 py-2 text-left text-xs transition-colors border ${
              modo === 'user' ? 'bg-primary/[0.08] border-primary/30 font-medium' : 'hover:bg-muted/60 border-transparent'
            }`}
          >
            Uma pessoa fixa
          </button>
          <button
            type="button"
            onClick={() => onUpdate({ assignMode: 'team', assignedUserId: null, assignedUserName: '' })}
            className={`col-span-2 rounded-lg px-3 py-2 text-left text-xs transition-colors border ${
              modo === 'team' ? 'bg-primary/[0.08] border-primary/30 font-medium' : 'hover:bg-muted/60 border-transparent'
            }`}
          >
            Uma equipe (quem estiver menos ocupado)
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">
          {modo === 'auto'
            ? 'Segue as regras de distribuição (responsável do contato, rodízio, carga de trabalho).'
            : modo === 'team'
              ? 'Entre as pessoas escolhidas, vai para quem tiver menos atendimento aberto agora.'
              : 'A conversa vai sempre para a mesma pessoa, independente da fila.'}
        </p>
      </div>

      {modo === 'team' && <EquipeConfig node={node} onUpdate={onUpdate} />}

      {modo === 'user' && (
        <div>
          <SectionLabel>Atendente</SectionLabel>
          {isError ? (
            <p className="text-[11px] text-amber-700 mt-1 leading-snug">
              Você não tem permissão para ver a equipe. Peça a um administrador para escolher a pessoa,
              ou use a distribuição automática.
            </p>
          ) : (
            <>
              <Select
                value={node.data.assignedUserId ? String(node.data.assignedUserId) : undefined}
                onValueChange={(v) => {
                  const u = ativos.find((x) => x.id === Number(v));
                  onUpdate({
                    assignMode: 'user',
                    assignedUserId: Number(v),
                    assignedUserName: u?.name || u?.email || '',
                  });
                }}
              >
                <SelectTrigger className="text-sm h-9 mt-1">
                  <SelectValue placeholder={isLoading ? 'Carregando…' : 'Escolher pessoa...'} />
                </SelectTrigger>
                <SelectContent>
                  {ativos.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.name || u.email}
                    </SelectItem>
                  ))}
                  {!isLoading && ativos.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">Ninguém ativo na equipe</div>
                  )}
                </SelectContent>
              </Select>
              {/* Quem foi desativado depois que o fluxo foi montado some da
                  lista, e o motor recusa o passo. Melhor avisar aqui. */}
              {node.data.assignedUserId && !ativos.some((u) => u.id === node.data.assignedUserId) && !isLoading && (
                <p className="text-[11px] text-amber-700 mt-1.5 leading-snug">
                  {node.data.assignedUserName || `Atendente #${node.data.assignedUserId}`} não está mais ativo na
                  equipe — escolha outra pessoa, senão este passo vai falhar.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex items-start justify-between gap-3 rounded-lg border px-3 py-2.5">
        <div className="min-w-0">
          <Label htmlFor="parar-robo" className="text-xs font-medium cursor-pointer">
            Parar o robô e entregar o atendimento
          </Label>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            O fluxo termina aqui e a conversa fica com o humano. Sem isso, o robô continua
            falando pelos próximos passos.
          </p>
        </div>
        <Switch
          id="parar-robo"
          className="shrink-0 mt-0.5"
          checked={!!paraRobo}
          onCheckedChange={(v) => onUpdate({ stopBot: v, actionType: v ? 'handoff' : 'assign' })}
        />
      </div>
    </div>
  );
}

/**
 * Onde a oportunidade nasce, ou para onde ela vai.
 *
 * Guarda o NOME do funil e da etapa junto com o id (`pipelineName`,
 * `stageName`): é o que o cartão do canvas mostra sem ter de buscar o CRM
 * inteiro para traduzir um número.
 */
function OportunidadeConfig({
  node,
  onUpdate,
  criando,
}: {
  node: Node;
  onUpdate: (field: string | Record<string, any>, value?: any) => void;
  /** `true` no passo que cria; `false` no que só move de etapa. */
  criando: boolean;
}) {
  const funis = trpc.crm.pipelines.list.useQuery(undefined, { retry: false });
  const pipelineId = Number(node.data.pipelineId) || undefined;
  const etapas = trpc.crm.stages.list.useQuery(
    { pipelineId: pipelineId! },
    { enabled: !!pipelineId, retry: false },
  );

  if (funis.isError) {
    return (
      <p className="text-[11px] text-amber-700 leading-snug">
        Você não tem permissão para ver os funis do CRM. Peça a um administrador para configurar este passo.
      </p>
    );
  }

  const listaDeFunis = (funis.data ?? []) as Array<{ id: number; name: string }>;
  const listaDeEtapas = (etapas.data ?? []) as Array<{ id: number; name: string }>;

  return (
    <div className="space-y-3">
      <div>
        <SectionLabel>Funil</SectionLabel>
        <Select
          value={pipelineId ? String(pipelineId) : undefined}
          onValueChange={(v) => {
            const f = listaDeFunis.find((x) => x.id === Number(v));
            // Etapa é de um funil só: trocar de funil invalida a escolhida.
            onUpdate({ pipelineId: Number(v), pipelineName: f?.name ?? '', stageId: null, stageName: '' });
          }}
        >
          <SelectTrigger className="text-sm h-9 mt-1">
            <SelectValue placeholder={funis.isLoading ? 'Carregando…' : 'Escolher funil...'} />
          </SelectTrigger>
          <SelectContent>
            {listaDeFunis.map((f) => (
              <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!criando && (
          <p className="text-[11px] text-muted-foreground mt-1">
            Só para achar a oportunidade certa quando o contato tem negócio em mais de um funil.
          </p>
        )}
      </div>

      <div>
        <SectionLabel>{criando ? 'Etapa inicial' : 'Mover para a etapa'}</SectionLabel>
        <Select
          value={node.data.stageId ? String(node.data.stageId) : undefined}
          onValueChange={(v) => {
            const e = listaDeEtapas.find((x) => x.id === Number(v));
            onUpdate({ stageId: Number(v), stageName: e?.name ?? '' });
          }}
          disabled={!pipelineId}
        >
          <SelectTrigger className="text-sm h-9 mt-1">
            <SelectValue placeholder={pipelineId ? 'Escolher etapa...' : 'Escolha o funil antes'} />
          </SelectTrigger>
          <SelectContent>
            {listaDeEtapas.map((e) => (
              <SelectItem key={e.id} value={String(e.id)}>{e.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {criando && (
        <>
          <div>
            <SectionLabel>Nome do negócio</SectionLabel>
            <Input
              value={node.data.title || ''}
              onChange={(e) => handleTitulo(e.target.value)}
              placeholder="Oportunidade de {{nome}}"
              className="text-sm h-9 mt-1"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Aceita variáveis do contato. Em branco, vira “Oportunidade de {'{{nome}}'}”.
            </p>
          </div>
          <div>
            <SectionLabel>Valor (opcional)</SectionLabel>
            <Input
              value={node.data.dealValue ?? ''}
              onChange={(e) => onUpdate('dealValue', e.target.value)}
              placeholder="2500"
              inputMode="decimal"
              className="text-sm h-9 mt-1"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Só números. Em branco, o negócio nasce sem valor.
            </p>
          </div>
          <div>
            <SectionLabel>Se o contato já tiver oportunidade aberta</SectionLabel>
            <Select
              value={node.data.seJaExistir || 'move'}
              onValueChange={(v) => onUpdate('seJaExistir', v)}
            >
              <SelectTrigger className="text-sm h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="move">Mover a existente para esta etapa</SelectItem>
                <SelectItem value="skip">Não fazer nada</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              Nunca cria uma segunda — o comercial acabaria com duas fichas do mesmo negócio.
            </p>
          </div>
        </>
      )}
    </div>
  );

  function handleTitulo(v: string) {
    onUpdate('title', v);
  }
}

/** Quem forma a fila da equipe neste passo. */
function EquipeConfig({
  node,
  onUpdate,
}: {
  node: Node;
  onUpdate: (field: string | Record<string, any>, value?: any) => void;
}) {
  const { data: usuarios = [], isError } = trpc.users.list.useQuery(undefined, { retry: false }) as {
    data?: Array<{ id: number; name: string | null; email: string; isActive: boolean }>;
    isError: boolean;
  };
  const escolhidos: number[] = Array.isArray(node.data.teamUserIds) ? node.data.teamUserIds : [];
  const ativos = usuarios.filter((u) => u.isActive);

  if (isError) {
    return (
      <p className="text-[11px] text-amber-700 leading-snug">
        Você não tem permissão para ver a equipe. Peça a um administrador para montar a fila deste passo.
      </p>
    );
  }

  return (
    <div>
      <SectionLabel>Quem atende esta fila</SectionLabel>
      <div className="mt-1 space-y-0.5 max-h-56 overflow-y-auto rounded-md border p-1">
        {ativos.map((u) => {
          const marcado = escolhidos.includes(u.id);
          return (
            <button
              key={u.id}
              type="button"
              onClick={() =>
                onUpdate(
                  'teamUserIds',
                  marcado ? escolhidos.filter((id) => id !== u.id) : [...escolhidos, u.id],
                )
              }
              className={`w-full flex items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors ${
                marcado ? 'bg-primary/[0.08] font-medium' : 'hover:bg-muted/60'
              }`}
            >
              <span
                className={`w-3.5 h-3.5 rounded border shrink-0 flex items-center justify-center ${
                  marcado ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'
                }`}
              >
                {marcado && <Check className="w-2.5 h-2.5" />}
              </span>
              <span className="truncate">{u.name || u.email}</span>
            </button>
          );
        })}
        {ativos.length === 0 && (
          <p className="px-2 py-3 text-xs text-muted-foreground">Ninguém ativo na equipe.</p>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground mt-1.5">
        A conversa vai para quem tiver menos atendimento aberto no momento.
      </p>
    </div>
  );
}

/** Qual evento personalizado o passo registra na linha do tempo. */
function EventoConfig({
  node,
  onUpdate,
}: {
  node: Node;
  onUpdate: (field: string | Record<string, any>, value?: any) => void;
}) {
  const { data: eventos = [], isLoading, isError } = trpc.customEvents.list.useQuery(undefined, {
    retry: false,
  }) as { data?: Array<{ id: number; name: string }>; isLoading: boolean; isError: boolean };

  if (isError) {
    return (
      <p className="text-[11px] text-amber-700 leading-snug">
        Você não tem permissão para ver os eventos personalizados deste workspace.
      </p>
    );
  }

  return (
    <div>
      <SectionLabel>Evento</SectionLabel>
      <Select
        value={node.data.customEventId ? String(node.data.customEventId) : undefined}
        onValueChange={(v) => {
          const e = eventos.find((x) => x.id === Number(v));
          // O nome vai junto para o cartão do canvas não precisar buscar.
          onUpdate({ customEventId: Number(v), customEventName: e?.name ?? '' });
        }}
      >
        <SelectTrigger className="text-sm h-9 mt-1">
          <SelectValue placeholder={isLoading ? 'Carregando…' : 'Escolher evento...'} />
        </SelectTrigger>
        <SelectContent>
          {eventos.map((e) => (
            <SelectItem key={e.id} value={String(e.id)}>{e.name}</SelectItem>
          ))}
          {!isLoading && eventos.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Nenhum evento cadastrado — crie em Eventos Personalizados.
            </div>
          )}
        </SelectContent>
      </Select>
      <p className="text-[11px] text-muted-foreground mt-1">
        Aparece na linha do tempo do contato e nos relatórios de evento.
      </p>
    </div>
  );
}

/** Para qual fluxo o contato segue. */
function FluxoDestinoConfig({
  node,
  onUpdate,
}: {
  node: Node;
  onUpdate: (field: string | Record<string, any>, value?: any) => void;
}) {
  const { data: fluxos = [], isLoading } = trpc.flows.list.useQuery(undefined, { retry: false }) as {
    data?: Array<{ id: number; name: string; channel?: string | null }>;
    isLoading: boolean;
  };
  return (
    <div className="space-y-3">
      <div>
        <SectionLabel>Fluxo de destino</SectionLabel>
        <Select
          value={node.data.targetFlowId ? String(node.data.targetFlowId) : undefined}
          onValueChange={(v) => {
            const f = fluxos.find((x) => x.id === Number(v));
            onUpdate({ targetFlowId: Number(v), targetFlowName: f?.name ?? '' });
          }}
        >
          <SelectTrigger className="text-sm h-9 mt-1">
            <SelectValue placeholder={isLoading ? 'Carregando…' : 'Escolher fluxo...'} />
          </SelectTrigger>
          <SelectContent>
            {fluxos.map((f) => (
              <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <SectionLabel>Se o contato já estiver no fluxo de destino</SectionLabel>
        <Select
          value={node.data.onAlreadyEnrolled || 'skip'}
          onValueChange={(v) => onUpdate('onAlreadyEnrolled', v)}
        >
          <SelectTrigger className="text-sm h-9 mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="skip">Não fazer nada</SelectItem>
            <SelectItem value="restart">Recomeçar do início</SelectItem>
            <SelectItem value="allow">Deixar rodar em paralelo</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Um fluxo pode chamar outro até cinco níveis — depois disso o motor para. Apontar um fluxo
        para ele mesmo é permitido, e é esse limite que impede o laço infinito.
      </p>
    </div>
  );
}

/**
 * A regra que divide o caminho em Sim e Não.
 *
 * O Select antigo oferecia "Status" e "Engajamento" — nenhum dos dois existia
 * no motor: a condição caía no `return false` final e TODO MUNDO seguia pelo
 * Não, sem aviso nenhum. Aqui só entra o que o motor sabe avaliar.
 */
function CondicaoConfig({
  node,
  onUpdate,
}: {
  node: Node;
  onUpdate: (field: string | Record<string, any>, value?: any) => void;
}) {
  const tipo = String(node.data.conditionType ?? node.data.field ?? 'tag');
  const escolher = (v: string) => onUpdate({ conditionType: v, field: v });

  return (
    <div className="space-y-3">
      <div>
        <SectionLabel>O que verificar</SectionLabel>
        <Select value={tipo} onValueChange={escolher}>
          <SelectTrigger className="text-sm h-9 mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tag">O contato tem uma etiqueta</SelectItem>
            <SelectItem value="field">Um campo do contato</SelectItem>
            <SelectItem value="score">A pontuação do contato</SelectItem>
            <SelectItem value="ad_source">De qual anúncio o contato veio</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {tipo === 'tag' && (
        <>
          <div>
            <SectionLabel>Etiqueta</SectionLabel>
            <Input
              value={node.data.value || ''}
              onChange={(e) => onUpdate('value', e.target.value)}
              placeholder="lead-quente"
              className="text-sm h-9 mt-1"
            />
          </div>
          <div>
            <SectionLabel>Segue pelo Sim quando</SectionLabel>
            <Select value={node.data.operator || 'equals'} onValueChange={(v) => onUpdate('operator', v)}>
              <SelectTrigger className="text-sm h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="equals">Tem a etiqueta</SelectItem>
                <SelectItem value="not-equals">Não tem a etiqueta</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {tipo === 'field' && (
        <>
          <div>
            <SectionLabel>Campo</SectionLabel>
            <Input
              value={node.data.fieldKey || ''}
              onChange={(e) => onUpdate('fieldKey', e.target.value)}
              placeholder="city"
              className="text-sm h-9 mt-1"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Nome do campo no cadastro do contato (ex.: city, companyName, sector).
            </p>
          </div>
          <div>
            <SectionLabel>Comparação</SectionLabel>
            <Select value={node.data.operator || 'equals'} onValueChange={(v) => onUpdate('operator', v)}>
              <SelectTrigger className="text-sm h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="equals">É igual a</SelectItem>
                <SelectItem value="not-equals">É diferente de</SelectItem>
                <SelectItem value="contains">Contém</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <SectionLabel>Valor</SectionLabel>
            <Input
              value={node.data.value || ''}
              onChange={(e) => onUpdate('value', e.target.value)}
              placeholder="Cuiabá"
              className="text-sm h-9 mt-1"
            />
          </div>
        </>
      )}

      {tipo === 'score' && (
        <div className="flex gap-2">
          <div className="flex-1">
            <SectionLabel>Pontuação</SectionLabel>
            <Select value={node.data.scoreOperator || 'gte'} onValueChange={(v) => onUpdate('scoreOperator', v)}>
              <SelectTrigger className="text-sm h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gte">É pelo menos</SelectItem>
                <SelectItem value="gt">Passa de</SelectItem>
                <SelectItem value="lte">É no máximo</SelectItem>
                <SelectItem value="lt">É menor que</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-24">
            <SectionLabel>Pontos</SectionLabel>
            <Input
              type="number"
              value={node.data.scoreValue ?? 50}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                onUpdate('scoreValue', Number.isFinite(n) ? n : 0);
              }}
              className="text-sm h-9 mt-1"
            />
          </div>
        </div>
      )}

      {tipo === 'ad_source' && (
        <div>
          <SectionLabel>ID do anúncio (opcional)</SectionLabel>
          <Input
            value={node.data.adId || ''}
            onChange={(e) => onUpdate('adId', e.target.value)}
            placeholder="Em branco = qualquer anúncio"
            className="text-sm h-9 mt-1"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Em branco, o Sim é de quem chegou por <strong>qualquer</strong> anúncio de clique-para-WhatsApp.
            Com um ID, só de quem veio daquele. O ID aparece no relatório de anúncios.
          </p>
        </div>
      )}
    </div>
  );
}

function ContentConfig({ node, onUpdate }: { node: Node; onUpdate: (field: string | Record<string, any>, value?: any) => void }) {
  const contentSource = node.data.contentSource || 'custom';
  const { data: templates } = trpc.templates.list.useQuery(undefined, {
    enabled: contentSource === 'template',
  });

  const selectedTemplate = templates?.find((t: any) => t.id === node.data.templateId);

  // Detecta se o nó tem botões roteáveis (template QUICK_REPLY ou custom buttons).
  // Sem botões, o campo de fallback message não faz sentido.
  const hasRoutableButtons = (() => {
    if (contentSource === 'template') {
      const tplButtons: any[] = Array.isArray(selectedTemplate?.buttons) ? selectedTemplate!.buttons : [];
      return tplButtons.some((b) => b.type === 'QUICK_REPLY');
    }
    const customButtons: any[] = node.data.messageContent?.buttons || [];
    return customButtons.some((b) => b.type === 'text' || !b.type);
  })();

  return (
    <div className="space-y-3">
      <div>
        <SectionLabel>Origem do conteúdo</SectionLabel>
        <div className="grid grid-cols-2 gap-1.5 mt-1">
          <button
            type="button"
            onClick={() => onUpdate('contentSource', 'custom')}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-left text-xs transition-colors border
              ${contentSource === 'custom'
                ? 'bg-primary/[0.08] border-primary/30 font-medium'
                : 'hover:bg-muted/60 border-transparent'
              }`}
          >
            <MessageCircle className="w-3.5 h-3.5 text-green-600 shrink-0" strokeWidth={1.8} />
            Personalizada
          </button>
          <button
            type="button"
            onClick={() => onUpdate('contentSource', 'template')}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-left text-xs transition-colors border
              ${contentSource === 'template'
                ? 'bg-primary/[0.08] border-primary/30 font-medium'
                : 'hover:bg-muted/60 border-transparent'
              }`}
          >
            <FileText className="w-3.5 h-3.5 text-blue-600 shrink-0" strokeWidth={1.8} />
            Template Meta
          </button>
        </div>
      </div>

      {contentSource === 'custom' && (
        <div>
          <SectionLabel>Mensagem</SectionLabel>
          <div className="mt-1">
            <MessageEditor
              content={node.data.messageContent || { type: 'text', text: '' }}
              onChange={(content) => onUpdate('messageContent', content)}
            />
          </div>
        </div>
      )}

      {contentSource === 'template' && (
        <div className="space-y-3">
          <div>
            <SectionLabel>Template</SectionLabel>
            <Select
              value={node.data.templateId ? String(node.data.templateId) : undefined}
              onValueChange={(v) => {
                const tpl = templates?.find((t: any) => t.id === Number(v));
                const metaButtons = Array.isArray(tpl?.buttons) ? tpl.buttons : [];
                const mappedButtons = metaButtons.map((btn: any, i: number) => {
                  const type = btn.type === 'URL' ? 'url' as const
                    : btn.type === 'PHONE_NUMBER' ? 'phone' as const
                    : 'text' as const;
                  const value = btn.type === 'URL' ? (btn.url || '')
                    : btn.type === 'PHONE_NUMBER' ? (btn.phone_number || btn.phoneNumber || '')
                    : '';
                  return { id: stableButtonId(btn.text || ''), text: btn.text || '', type, value };
                });
                onUpdate({
                  templateId: Number(v),
                  templateName: tpl?.name || '',
                  templateBody: tpl?.bodyText || '',
                  templateButtons: metaButtons.map((btn: any) => ({
                    type: typeof btn.type === 'string' ? btn.type : '',
                    text: typeof btn.text === 'string' ? btn.text : '',
                    label: typeof btn.label === 'string' ? btn.label : undefined,
                    url: typeof btn.url === 'string' ? btn.url : undefined,
                    phone_number: typeof btn.phone_number === 'string' ? btn.phone_number
                      : typeof btn.phoneNumber === 'string' ? btn.phoneNumber
                      : undefined,
                  })),
                  templateFooter: tpl?.footerText || '',
                  templateHeaderType: tpl?.headerType || null,
                  messageContent: {
                    type: 'text' as const,
                    text: tpl?.bodyText || '',
                    buttons: mappedButtons,
                  },
                });
              }}
            >
              <SelectTrigger className="text-sm h-9 mt-1">
                <SelectValue placeholder="Selecionar template..." />
              </SelectTrigger>
              <SelectContent>
                {templates?.map((t: any) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    <div className="flex items-center gap-2">
                      <span>{t.name}</span>
                      {t.status && (
                        <Badge variant={STATUS_BADGE[t.status]?.variant || 'outline'} className="text-[9px] px-1 py-0 h-4">
                          {STATUS_BADGE[t.status]?.label || t.status}
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
                {templates?.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    Nenhum template encontrado
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedTemplate && (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">{selectedTemplate.name}</span>
                <Badge variant={STATUS_BADGE[selectedTemplate.status]?.variant || 'outline'} className="text-[9px] px-1.5 py-0 h-4">
                  {STATUS_BADGE[selectedTemplate.status]?.label || selectedTemplate.status}
                </Badge>
              </div>
              {selectedTemplate.bodyText && (
                <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto">
                  {selectedTemplate.bodyText}
                </p>
              )}
              {selectedTemplate.headerType && selectedTemplate.headerType !== 'TEXT' && (
                <div className="text-[10px] text-muted-foreground">
                  Header: {selectedTemplate.headerType}
                </div>
              )}
              {(selectedTemplate.variableCount ?? 0) > 0 && (
                <p className="text-[10px] text-muted-foreground">
                  {selectedTemplate.variableCount} variável(is) — preencha abaixo
                </p>
              )}
            </div>
          )}

          {selectedTemplate && (selectedTemplate.variableCount ?? 0) > 0 && (
            <div>
              <SectionLabel>Variáveis do template</SectionLabel>
              <div className="space-y-2 mt-1">
                {Array.from({ length: selectedTemplate.variableCount ?? 0 }, (_, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-8 shrink-0 font-mono">{`{{${i + 1}}}`}</span>
                    <Input
                      value={(node.data.templateVariables || {})[`${i + 1}`] || ''}
                      onChange={(e) => {
                        const vars = { ...(node.data.templateVariables || {}), [`${i + 1}`]: e.target.value };
                        onUpdate('templateVariables', vars);
                      }}
                      placeholder={`Valor para variável ${i + 1}`}
                      className="text-sm h-8"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mensagem de fallback quando o cliente responde com texto livre em vez
          de clicar em um dos botões. Sem ela, o fluxo é silenciosamente
          completado e o cliente fica sem resposta. */}
      {hasRoutableButtons && (
        <div className="border-t pt-3">
          <SectionLabel>Se nenhum botão for clicado</SectionLabel>
          <Textarea
            value={node.data.fallbackMessage || ''}
            onChange={(e) => onUpdate('fallbackMessage', e.target.value)}
            placeholder="Ex: Não entendi. Por favor, escolha uma das opções acima clicando no botão."
            className="text-sm min-h-[60px] mt-1"
            rows={2}
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Enviada se o cliente responder com texto livre em vez de clicar em um dos botões. Deixe vazio para não enviar nada.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Escolha do campo personalizado onde a resposta coletada é gravada.
 *
 * Era um Input de texto livre: um erro de digitação passava despercebido —
 * o fluxo validava a resposta, avançava normalmente, e o valor era descartado
 * com apenas um aviso no log do servidor. Com a lista carregada, o nome vem
 * pronto e o descasamento deixa de ser possível.
 *
 * Fallback deliberado: `customFields.listDefs` exige permissão de edição de
 * contatos, que nem todo editor de fluxo tem. Sem a lista (sem permissão, erro
 * de rede, ou tenant que ainda não criou campos), volta ao Input livre em vez
 * de bloquear a configuração do nó.
 */
function SaveToFieldPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data: fields = [], isError } = trpc.customFields.listDefs.useQuery(undefined, {
    retry: false,
  }) as { data?: Array<{ id: number; key: string; name: string }>; isError: boolean };

  // Valor legado que não bate com nenhum campo atual: mantém o texto editável
  // para não apagar silenciosamente a config de um fluxo já publicado.
  const isKnown = fields.some((f) => f.key === value);
  const useFreeText = isError || fields.length === 0 || (!!value && !isKnown);

  if (useFreeText) {
    return (
      <div>
        <SectionLabel>Salvar no campo</SectionLabel>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Nome do campo personalizado"
          className="text-sm h-9 mt-1"
        />
        {!!value && !isKnown && fields.length > 0 && (
          <p className="text-[11px] text-amber-600 mt-1">
            Nenhum campo personalizado com este nome. A resposta não será salva.
          </p>
        )}
      </div>
    );
  }

  return (
    <div>
      <SectionLabel>Salvar no campo</SectionLabel>
      <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? '' : v)}>
        <SelectTrigger className="text-sm h-9 mt-1">
          <SelectValue placeholder="Selecione um campo" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Não salvar</SelectItem>
          {fields.map((f) => (
            <SelectItem key={f.id} value={f.key}>
              {f.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function NodeConfigPanel({ node, onClose, onUpdate, onDelete, onSaveNode }: NodeConfigPanelProps) {
  if (!node) return null;

  const meta = NODE_META[node.type || ''] || { icon: MessageCircle, label: node.type, accent: 'text-gray-600', bar: 'bg-gray-500' };
  const Icon = meta.icon;
  const pendencia = pendenciaDoNo(node.type, node.data);

  const handleUpdate = (field: string | Record<string, any>, value?: any) => {
    if (typeof field === 'object') {
      onUpdate(node.id, { ...node.data, ...field });
    } else {
      onUpdate(node.id, { ...node.data, [field]: value });
    }
  };

  return (
    <div className="space-y-5">
      {/* Que bloco é este, e o que ele está fazendo agora */}
      <div className="flex items-start gap-2.5">
        <div className={`shrink-0 mt-0.5 ${meta.accent}`}>
          <Icon className="w-4 h-4" strokeWidth={1.8} />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-medium text-foreground">{meta.label}</div>
          <div className="text-[11px] text-muted-foreground leading-snug">
            {resumoDoNo(node.type, node.data)}
          </div>
        </div>
      </div>

      {/* A mesma pendência do cartão e do painel "Antes de ativar" — para a
          pessoa não precisar cruzar duas listas para saber o que falta. */}
      {pendencia && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-2.5">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-[11px] text-amber-800 leading-snug">{pendencia.longa}</p>
        </div>
      )}

      {/* Nome */}
      <div>
        <SectionLabel>Nome</SectionLabel>
        <Input
          value={node.data.label || ''}
          onChange={(e) => handleUpdate('label', e.target.value)}
          placeholder="Nome do bloco"
          className="text-sm h-9 mt-1"
        />
      </div>

      {/* ─── Configurações por tipo ─── */}

      {node.type === 'content' && (
        <ContentConfig node={node} onUpdate={handleUpdate} />
      )}

      {node.type === 'action' && (
        <div className="space-y-3">
          <div>
            <SectionLabel>O que este passo faz</SectionLabel>
            <Select value={node.data.actionType || 'add-tag'} onValueChange={(v) => handleUpdate('actionType', v)}>
              <SelectTrigger className="text-sm h-9 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="add-tag">Adicionar etiqueta</SelectItem>
                <SelectItem value="remove-tag">Remover etiqueta</SelectItem>
                <SelectItem value="set-field">Atualizar campo do contato</SelectItem>
                <SelectItem value="update_score">Pontuar o contato</SelectItem>
                <SelectItem value="handoff">Passar para atendente</SelectItem>
                <SelectItem value="assign">Atribuir a um atendente (sem parar o robô)</SelectItem>
                <SelectItem value="create_deal">Criar oportunidade no CRM</SelectItem>
                <SelectItem value="move_deal_stage">Mover etapa do funil</SelectItem>
                <SelectItem value="send_proposal">Enviar a proposta do negócio</SelectItem>
                <SelectItem value="note">Nota interna na conversa</SelectItem>
                <SelectItem value="timeline_event">Registrar evento na linha do tempo</SelectItem>
                <SelectItem value="enroll_in_flow">Mandar para outro fluxo</SelectItem>
                <SelectItem value="close_conversation">Encerrar com motivo</SelectItem>
                <SelectItem value="webhook">Avisar outro sistema</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(node.data.actionType === 'add-tag' || node.data.actionType === 'remove-tag') && (
            <div>
              <SectionLabel>Etiquetas</SectionLabel>
              <TagMultiPicker
                value={Array.isArray(node.data.tagIds) ? node.data.tagIds : (node.data.tagId ? [node.data.tagId] : [])}
                onChange={(tagIds) => handleUpdate({ tagIds, tagId: tagIds[0] ?? null })}
                onChangeNames={(names) => handleUpdate('value', names.join(', '))}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                {node.data.actionType === 'add-tag'
                  ? 'Escolha uma ou mais etiquetas para marcar o contato. Dá para criar aqui mesmo.'
                  : 'Escolha uma ou mais etiquetas para tirar do contato.'}
              </p>
            </div>
          )}

          {['assign', 'assign_agent', 'handoff'].includes(node.data.actionType) && (
            <AtribuicaoConfig node={node} onUpdate={handleUpdate} />
          )}

          {node.data.actionType === 'create_deal' && (
            <OportunidadeConfig node={node} onUpdate={handleUpdate} criando />
          )}

          {node.data.actionType === 'move_deal_stage' && (
            <OportunidadeConfig node={node} onUpdate={handleUpdate} criando={false} />
          )}

          {node.data.actionType === 'send_proposal' && (
            <div>
              <SectionLabel>Mensagem que acompanha (opcional)</SectionLabel>
              <Textarea
                value={node.data.caption || ''}
                onChange={(e) => handleUpdate('caption', e.target.value)}
                placeholder="Segue a proposta que preparamos para você, {{nome}}."
                className="text-sm min-h-[70px] mt-1"
                rows={3}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Manda o PDF da proposta <strong>aprovada</strong> do negócio deste contato. Não gera nem
                assina nada — se não houver proposta pronta, o passo falha e avisa.
              </p>
            </div>
          )}

          {node.data.actionType === 'note' && (
            <div>
              <SectionLabel>Texto da nota</SectionLabel>
              <Textarea
                value={node.data.value || ''}
                onChange={(e) => handleUpdate('value', e.target.value)}
                placeholder="Lead pediu proposta pelo fluxo de boas-vindas."
                className="text-sm min-h-[70px] mt-1"
                rows={3}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Fica no histórico do Inbox como nota — o contato não recebe nada.
              </p>
            </div>
          )}

          {node.data.actionType === 'timeline_event' && (
            <EventoConfig node={node} onUpdate={handleUpdate} />
          )}

          {node.data.actionType === 'update_score' && (
            <div className="space-y-3">
              <div>
                <SectionLabel>Pontos</SectionLabel>
                <Input
                  type="number"
                  value={node.data.points ?? 10}
                  onChange={(e) => {
                    // Campo limpo vira NaN, que sobrevive à checagem `!== 0`
                    // da pendência, serializa como null e só falha lá no
                    // motor com "Pontos inválidos".
                    const n = parseInt(e.target.value, 10);
                    handleUpdate('points', Number.isFinite(n) ? n : 0);
                  }}
                  className="text-sm h-9 mt-1"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Positivo soma, negativo tira. Zero não muda nada.
                </p>
              </div>
              <div>
                <SectionLabel>Motivo</SectionLabel>
                <Input
                  value={node.data.reason || ''}
                  onChange={(e) => handleUpdate('reason', e.target.value)}
                  placeholder="pediu-proposta"
                  className="text-sm h-9 mt-1"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Aparece no detalhamento da pontuação do contato.
                </p>
              </div>
            </div>
          )}

          {node.data.actionType === 'enroll_in_flow' && (
            <FluxoDestinoConfig node={node} onUpdate={handleUpdate} />
          )}

          {node.data.actionType === 'close_conversation' && (
            <div className="space-y-3">
              <div>
                <SectionLabel>Motivo do encerramento</SectionLabel>
                <Select
                  value={node.data.closeReason || 'resolved'}
                  // Limpa o `resolved` escolhido para o motivo anterior: ele
                  // sobrevivia à troca e o motor o respeita (`?? deduzido`),
                  // fechando como resolvido sob um motivo que não resolve.
                  onValueChange={(v) => handleUpdate({ closeReason: v, resolved: undefined })}
                >
                  <SelectTrigger className="text-sm h-9 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[...CLOSE_REASONS.resolved, ...CLOSE_REASONS.unresolved]
                      .filter((r, i, arr) => arr.findIndex((x) => x.value === r.value) === i)
                      .map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              {/* "Resolveu?" é outra pergunta: "Encaminhado a outra equipe" e
                  "Outro" valem para os dois desfechos, e é o que alimenta a
                  taxa de resolução do relatório. */}
              {['transferred', 'other'].includes(node.data.closeReason ?? '') && (
                <div className="flex items-start justify-between gap-3 rounded-lg border px-3 py-2.5">
                  <div className="min-w-0">
                    <Label htmlFor="resolveu" className="text-xs font-medium cursor-pointer">
                      Conta como resolvido
                    </Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                      Entra na taxa de resolução do relatório de atendimento.
                    </p>
                  </div>
                  <Switch
                    id="resolveu"
                    className="shrink-0 mt-0.5"
                    checked={node.data.resolved ?? false}
                    onCheckedChange={(v) => handleUpdate('resolved', v)}
                  />
                </div>
              )}
              <div>
                <SectionLabel>Observação (opcional)</SectionLabel>
                <Input
                  value={node.data.closedNote || ''}
                  onChange={(e) => handleUpdate('closedNote', e.target.value)}
                  placeholder="Contato só queria o link do curso"
                  className="text-sm h-9 mt-1"
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                O fluxo termina aqui: encerrar o atendimento e continuar falando seria contraditório.
              </p>
            </div>
          )}

          {node.data.actionType === 'set-field' && (
            <>
              <div>
                <SectionLabel>Campo</SectionLabel>
                <Select value={node.data.field || ''} onValueChange={(v) => handleUpdate('field', v)}>
                  <SelectTrigger className="text-sm h-9 mt-1">
                    <SelectValue placeholder="Escolher campo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Espelha ALLOWED_CONTACT_FIELDS do motor. Campo fora dessa
                        lista faz o passo falhar em execução — melhor não oferecer. */}
                    <SelectItem value="notes">Observações</SelectItem>
                    <SelectItem value="companyName">Empresa</SelectItem>
                    <SelectItem value="city">Cidade</SelectItem>
                    <SelectItem value="state">Estado</SelectItem>
                    <SelectItem value="companyType">Tipo de empresa</SelectItem>
                    <SelectItem value="sector">Setor</SelectItem>
                    <SelectItem value="jobTitle">Cargo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <SectionLabel>Valor a gravar</SectionLabel>
                <Input
                  value={node.data.value || ''}
                  onChange={(e) => handleUpdate('value', e.target.value)}
                  placeholder="Ex: Prefeitura de São José"
                  className="text-sm h-9 mt-1"
                />
              </div>
            </>
          )}

          {node.data.actionType === 'webhook' && (
            <div>
              <SectionLabel>URL de destino</SectionLabel>
              <Input
                value={node.data.value || ''}
                onChange={(e) => handleUpdate('value', e.target.value)}
                placeholder="https://"
                className="text-sm h-9 mt-1"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Enviamos nome, telefone e e-mail do contato num POST.
              </p>
            </div>
          )}
        </div>
      )}

      {node.type === 'condition' && (
        <CondicaoConfig node={node} onUpdate={handleUpdate} />
      )}

      {node.type === 'delay' && (
        <div className="space-y-3">
          <div>
            <SectionLabel>Modo</SectionLabel>
            <Select value={node.data.delayMode || 'duration'} onValueChange={(v) => handleUpdate('delayMode', v)}>
              <SelectTrigger className="text-sm h-9 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="duration">Esperar por tempo</SelectItem>
                <SelectItem value="until_time">Até horário específico</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(node.data.delayMode || 'duration') === 'duration' ? (
            <div>
              <SectionLabel>Duração</SectionLabel>
              <div className="flex gap-2 mt-1">
                <Input
                  type="number"
                  value={node.data.duration || 5}
                  onChange={(e) => handleUpdate('duration', parseInt(e.target.value))}
                  className="flex-1 text-sm h-9"
                />
                <Select value={node.data.unit || 'minutes'} onValueChange={(v) => handleUpdate('unit', v)}>
                  <SelectTrigger className="w-28 text-sm h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seconds">Segundos</SelectItem>
                    <SelectItem value="minutes">Minutos</SelectItem>
                    <SelectItem value="hours">Horas</SelectItem>
                    <SelectItem value="days">Dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div>
              <SectionLabel>Horário</SectionLabel>
              <Input
                type="time"
                value={node.data.untilTime || '09:00'}
                onChange={(e) => handleUpdate('untilTime', e.target.value)}
                className="text-sm h-9 mt-1"
              />
              <p className="text-[11px] text-muted-foreground mt-1.5">Continua no próximo horário configurado</p>
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
            <Label htmlFor="office-hours" className="text-xs font-medium cursor-pointer">
              Respeitar horário comercial
            </Label>
            <Switch
              id="office-hours"
              checked={!!node.data.respectOfficeHours}
              onCheckedChange={(v) => handleUpdate('respectOfficeHours', v)}
            />
          </div>
        </div>
      )}

      {node.type === 'randomizer' && (
        <div className="space-y-3">
          <SectionLabel>Distribuição</SectionLabel>
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-muted-foreground w-16">Caminho A</span>
              <Input
                type="number"
                min="0"
                max="100"
                value={node.data.path1Probability || 50}
                onChange={(e) => handleUpdate('path1Probability', parseInt(e.target.value))}
                className="text-sm h-9 flex-1"
              />
              <span className="text-xs text-muted-foreground w-4">%</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-muted-foreground w-16">Caminho B</span>
              <div className="flex-1 text-sm text-muted-foreground bg-muted/50 rounded-md h-9 flex items-center px-3">
                {100 - (node.data.path1Probability || 50)}%
              </div>
              <span className="text-xs text-muted-foreground w-4" />
            </div>
          </div>
        </div>
      )}

      {node.type === 'target' && (
        <>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Este bloco saiu da prateleira porque o motor nunca soube executá-lo. Para filtrar
            quem segue adiante, use <strong>Dividir por condição</strong> e apague este passo.
          </p>
          <TargetConfig node={node} onUpdate={handleUpdate} />
        </>
      )}

      {node.type === 'ai_assistant' && node.data.modo === 'kb_only' && (
        <div className="space-y-3">
          <p className="text-[11px] text-muted-foreground leading-snug">
            Este passo responde <strong>só com o que estiver na base de conhecimento</strong>. Se a resposta
            não estiver lá, ele nem chama o modelo: manda a mensagem abaixo e segue pelo caminho
            “Não soube responder”.
          </p>
          <div>
            <SectionLabel>Instrução</SectionLabel>
            <Textarea
              value={node.data.systemPrompt || ''}
              onChange={(e) => handleUpdate('systemPrompt', e.target.value)}
              placeholder="Responda dúvidas sobre datas, valores e inscrição usando a base da empresa."
              className="text-sm min-h-[80px] mt-1"
              rows={3}
            />
          </div>
          <div>
            <SectionLabel>Quando não achar na base</SectionLabel>
            <Textarea
              value={node.data.fallbackMessage || ''}
              onChange={(e) => handleUpdate('fallbackMessage', e.target.value)}
              placeholder="Não tenho essa informação aqui. Vou te encaminhar para alguém da equipe."
              className="text-sm min-h-[60px] mt-1"
              rows={2}
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Deixe em branco para não mandar nada e apenas seguir pelo caminho de fallback.
            </p>
          </div>
        </div>
      )}

      {node.type === 'ai_assistant' && node.data.modo !== 'kb_only' && (
        <div className="space-y-3">
          <div>
            <SectionLabel>Prompt do sistema</SectionLabel>
            <Textarea
              value={node.data.systemPrompt || ''}
              onChange={(e) => handleUpdate('systemPrompt', e.target.value)}
              placeholder="Ex: Você é atendente de uma loja de roupas. Responda dúvidas sobre produtos, tamanhos, prazos de entrega e trocas. Seja cordial e objetivo."
              className="text-sm min-h-[120px] mt-1"
              rows={6}
              maxLength={4000}
            />
            <p className="text-[11px] text-muted-foreground mt-1">Descreva o papel, tom e limites do assistente. Guardrails de segurança são aplicados automaticamente.</p>
          </div>
          <div>
            <SectionLabel>Temperatura</SectionLabel>
            <Input
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={node.data.temperature ?? 0.7}
              onChange={(e) => handleUpdate('temperature', parseFloat(e.target.value))}
              className="text-sm h-9 mt-1"
            />
            <p className="text-[11px] text-muted-foreground mt-1">0 = preciso e conservador, 1 = criativo e variado</p>
          </div>
          <div>
            <SectionLabel>Mensagem de fallback</SectionLabel>
            <Textarea
              value={node.data.fallbackMessage || ''}
              onChange={(e) => handleUpdate('fallbackMessage', e.target.value)}
              placeholder="Desculpe, não consegui processar sua solicitação no momento. Um atendente irá ajudá-lo em breve."
              className="text-sm min-h-[60px] mt-1"
              rows={2}
            />
            <p className="text-[11px] text-muted-foreground mt-1">Enviada se a IA falhar, exceder limite, ou detectar problema de segurança</p>
          </div>
          <div>
            <SectionLabel>Condição de fallback</SectionLabel>
            <Input
              value={node.data.fallbackCondition || ''}
              onChange={(e) => handleUpdate('fallbackCondition', e.target.value)}
              placeholder="Ex: não sei, não consigo ajudar"
              className="text-sm h-9 mt-1"
            />
            <p className="text-[11px] text-muted-foreground mt-1">Se a IA responder com essas palavras, segue pelo caminho de fallback</p>
          </div>
          <div>
            <SectionLabel>Máx. interações por hora</SectionLabel>
            <Input
              type="number"
              min="1"
              max="20"
              value={node.data.maxInteractions ?? 5}
              onChange={(e) => handleUpdate('maxInteractions', parseInt(e.target.value))}
              className="text-sm h-9 mt-1"
            />
            <p className="text-[11px] text-muted-foreground mt-1">Limite de chamadas IA por contato (proteção contra loops)</p>
          </div>

          {/* ── Base de Conhecimento ── */}
          <div className="border-t pt-3 mt-1">
            <div className="flex items-center justify-between">
              <SectionLabel>Base de conhecimento</SectionLabel>
              <Switch
                checked={!!node.data.useKnowledgeBase}
                onCheckedChange={(v) => handleUpdate('useKnowledgeBase', v)}
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Injeta informacoes da base de conhecimento da empresa nas respostas da IA</p>
          </div>

          {/* ── Multi-turno (Conversa contínua com IA) ── */}
          <div className="border-t pt-3 mt-1">
            <div className="flex items-center justify-between">
              <SectionLabel>Conversa multi-turno</SectionLabel>
              <Switch
                checked={!!node.data.multiTurn}
                onCheckedChange={(v) => handleUpdate('multiTurn', v)}
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">A IA mantém conversa ativa com o cliente até resolver ou escalar</p>
          </div>

          {node.data.multiTurn && (
            <>
              <div>
                <SectionLabel>Máx. turnos de conversa</SectionLabel>
                <Input
                  type="number"
                  min="2"
                  max="30"
                  value={node.data.maxTurns ?? 10}
                  onChange={(e) => handleUpdate('maxTurns', parseInt(e.target.value))}
                  className="text-sm h-9 mt-1"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Após esse limite, escala para atendente humano</p>
              </div>
              <div>
                <SectionLabel>Mensagem de escalação</SectionLabel>
                <Textarea
                  value={node.data.escalationMessage || ''}
                  onChange={(e) => handleUpdate('escalationMessage', e.target.value)}
                  placeholder="Vou transferir você para um atendente humano. Aguarde um momento."
                  className="text-sm min-h-[60px] mt-1"
                  rows={2}
                />
                <p className="text-[11px] text-muted-foreground mt-1">Enviada quando excede turnos ou IA não consegue resolver</p>
              </div>
              <div>
                <SectionLabel>Palavras-chave de encerramento</SectionLabel>
                <Input
                  value={node.data.closingKeywords || ''}
                  onChange={(e) => handleUpdate('closingKeywords', e.target.value)}
                  placeholder="obrigado, resolvido, era isso"
                  className="text-sm h-9 mt-1"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Se a IA usar essas palavras na resposta, considera resolvido (separar por vírgula)</p>
              </div>
            </>
          )}
        </div>
      )}

      {node.type === 'ai_classifier' && (
        <div className="space-y-3">
          <div>
            <SectionLabel>Categorias</SectionLabel>
            <div className="space-y-1.5 mt-1">
              {(node.data.categories || []).map((cat: string, i: number) => (
                <div key={i} className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'][i % 8] }}
                  />
                  <Input
                    value={cat}
                    onChange={(e) => {
                      const cats = [...(node.data.categories || [])];
                      cats[i] = e.target.value;
                      handleUpdate('categories', cats);
                    }}
                    className="text-sm h-8 flex-1"
                    placeholder={`Categoria ${i + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const cats = (node.data.categories || []).filter((_: string, j: number) => j !== i);
                      handleUpdate('categories', cats);
                    }}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs"
                onClick={() => handleUpdate('categories', [...(node.data.categories || []), ''])}
              >
                + Adicionar categoria
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              A IA classifica a mensagem em uma destas categorias. Cada uma gera uma saida no fluxo.
            </p>
          </div>
          <div>
            <SectionLabel>Instrucoes adicionais (opcional)</SectionLabel>
            <Textarea
              value={node.data.classificationPrompt || ''}
              onChange={(e) => handleUpdate('classificationPrompt', e.target.value)}
              placeholder="Ex: Se a mensagem mencionar preço ou pagamento, classificar como Financeiro. Se mencionar defeito ou problema, classificar como Suporte."
              className="text-sm min-h-[80px] mt-1"
              rows={3}
            />
          </div>
        </div>
      )}

      {node.type === 'data_collection' && (
        <div className="space-y-3">
          <div>
            <SectionLabel>Tipo de dado</SectionLabel>
            <Select value={node.data.fieldType || 'text'} onValueChange={(v) => handleUpdate('fieldType', v)}>
              <SelectTrigger className="text-sm h-9 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">E-mail</SelectItem>
                <SelectItem value="phone">Telefone</SelectItem>
                <SelectItem value="cpf">CPF</SelectItem>
                <SelectItem value="number">Número</SelectItem>
                <SelectItem value="text">Texto livre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <SectionLabel>Mensagem de solicitação</SectionLabel>
            <Textarea
              value={node.data.promptMessage || ''}
              onChange={(e) => handleUpdate('promptMessage', e.target.value)}
              placeholder="Ex: Por favor, informe seu e-mail:"
              className="text-sm mt-1"
              rows={2}
            />
          </div>
          <div>
            <SectionLabel>Mensagem de erro</SectionLabel>
            <Input
              value={node.data.errorMessage || ''}
              onChange={(e) => handleUpdate('errorMessage', e.target.value)}
              placeholder="Ex: Formato inválido. Tente novamente."
              className="text-sm h-9 mt-1"
            />
          </div>
          <SaveToFieldPicker
            value={node.data.saveToField || ''}
            onChange={(v) => handleUpdate('saveToField', v)}
          />
          <div>
            <SectionLabel>Tentativas</SectionLabel>
            <Input
              type="number"
              min="1"
              max="5"
              value={node.data.maxRetries ?? 3}
              onChange={(e) => handleUpdate('maxRetries', parseInt(e.target.value))}
              className="text-sm h-9 mt-1"
            />
          </div>
        </div>
      )}

      {node.type === 'webhook_request' && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="w-24">
              <SectionLabel>Método</SectionLabel>
              <Select value={node.data.method || 'POST'} onValueChange={(v) => handleUpdate('method', v)}>
                <SelectTrigger className="text-sm h-9 mt-1 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <SectionLabel>URL</SectionLabel>
              <Input
                value={node.data.url || ''}
                onChange={(e) => handleUpdate('url', e.target.value)}
                placeholder="https://api.exemplo.com/endpoint"
                className="text-sm h-9 mt-1"
              />
            </div>
          </div>
          <div>
            <SectionLabel>Headers</SectionLabel>
            <Textarea
              value={node.data.headers || ''}
              onChange={(e) => handleUpdate('headers', e.target.value)}
              placeholder='{"Authorization": "Bearer ..."}'
              className="text-sm font-mono mt-1"
              rows={2}
            />
          </div>
          <div>
            <SectionLabel>Body</SectionLabel>
            <Textarea
              value={node.data.body || ''}
              onChange={(e) => handleUpdate('body', e.target.value)}
              placeholder='{"nome": "{{nome}}", "telefone": "{{telefone}}"}'
              className="text-sm font-mono mt-1"
              rows={3}
            />
            <p className="text-[11px] text-muted-foreground mt-1">Variáveis: {'{{nome}}'}, {'{{telefone}}'}, {'{{email}}'}</p>
          </div>
          <div>
            <SectionLabel>Salvar resposta em</SectionLabel>
            <Input
              value={node.data.saveResponseTo || ''}
              onChange={(e) => handleUpdate('saveResponseTo', e.target.value)}
              placeholder="Nome do campo personalizado (opcional)"
              className="text-sm h-9 mt-1"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              O valor vai para um campo personalizado do contato. Precisa existir em
              Campos Personalizados — se não existir, o passo avisa.
            </p>
          </div>

          {node.data.saveResponseTo && (
            <div>
              <SectionLabel>De onde tirar o valor</SectionLabel>
              <Input
                value={node.data.responsePath || ''}
                onChange={(e) => handleUpdate('responsePath', e.target.value)}
                placeholder="dados.status"
                className="text-sm h-9 mt-1"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Caminho dentro do JSON da resposta, separado por ponto. Em branco, guarda a
                resposta inteira — que quase nunca é o que se quer.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Footer actions */}
      <div className="flex gap-2 pt-2 border-t">
        {onSaveNode && (
          <Button
            size="sm"
            className="flex-1 gap-1.5 h-9"
            onClick={onSaveNode}
          >
            <Check className="w-3.5 h-3.5" />
            Salvar
          </Button>
        )}
        {onDelete && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 h-9 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => onDelete(node.id)}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Remover
          </Button>
        )}
      </div>
    </div>
  );
}
